// FullTOC.js — JobEng Academy · Engineering Books Library
// Modern, fully database-connected catalogue of all engineering books
// (paid Amazon titles + free openly-licensed PDFs). Replaces the old
// placeholder "table of contents" screen.
//
// Data: GET https://api.jobeng.ai/api/data/engineering_books
// Free books (pdf_url present) → open in PDFViewer ("Read")
// Paid books (amazon_link present) → open BookDetailScreen ("Buy")

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
  FlatList,
  Image,
  ActivityIndicator,
  RefreshControl,
  Linking,
  StatusBar,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API ─────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getHeaders = async () => {
  let tok = null;
  try {
    tok = (await AsyncStorage.getItem('auth_token')) ||
          (await AsyncStorage.getItem('userToken')) ||
          (await AsyncStorage.getItem('token'));
  } catch { /* ignore */ }
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// ─── Safe coercion helpers ───────────────────────────────────────────────────
const sNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const sStr = (v, d = '') => (v === null || v === undefined ? d : String(v));
const toArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return [];
    if (s.startsWith('[')) { try { const p = JSON.parse(s); if (Array.isArray(p)) return p; } catch {} }
    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }
  return [];
};

// Map a DB row → the book shape the cards + downstream screens expect.
const mapBook = (r) => {
  const price = sNum(r.price);
  const pdfUrl = sStr(r.pdf_url || r.pdfUrl);
  const isFree = price === 0 || r.pdf_available === true || !!pdfUrl;
  return {
    id: sStr(r.id || Math.random().toString(36).slice(2)),
    title: sStr(r.title || r.name, 'Untitled Book'),
    author: sStr(r.author, 'Unknown'),
    edition: sStr(r.edition),
    publisher: sStr(r.publisher),
    year: sStr(r.year),
    isbn: sStr(r.isbn),
    category: sStr(r.category, 'general'),
    pages: sNum(r.pages),
    level: sStr(r.level, 'All Levels'),
    color: sStr(r.color, '#0e4e96'),
    icon: sStr(r.icon, 'book'),
    coverImage: sStr(r.cover_image || r.coverImage || r.thumbnail),
    description: sStr(r.description),
    topics: toArray(r.topics),
    amazonLink: sStr(r.amazon_link || r.amazonLink),
    pdfAvailable: r.pdf_available === true || !!pdfUrl,
    pdfUrl,
    rating: sNum(r.rating, 4.5),
    price,
    isFree,
    _raw: r,
  };
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'free', label: 'Free to Read' },
  { key: 'paid', label: 'Buy' },
];

const FullTOC = () => {
  const navigation = useNavigation();
  const route = useRoute();

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dbConnected, setDbConnected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [category, setCategory] = useState('all');

  const loadBooks = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    setError(null);
    try {
      const h = await getHeaders();
      const res = await fetch(`${API_BASE}/api/data/engineering_books?limit=100`, { headers: h });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json().catch(() => null);
      const rows = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      const mapped = rows.map(mapBook);
      if (mapped.length) {
        setBooks(mapped);
        setDbConnected(true);
        try { await AsyncStorage.setItem('cache_books_library', JSON.stringify(mapped)); } catch {}
      } else {
        const cached = await AsyncStorage.getItem('cache_books_library');
        setBooks(cached ? JSON.parse(cached) : []);
        setDbConnected(false);
      }
    } catch (e) {
      console.error('Books load failed:', e.message);
      const cached = await AsyncStorage.getItem('cache_books_library');
      if (cached) {
        setBooks(JSON.parse(cached));
        setDbConnected(false);
      } else {
        setError('Could not load the library. Pull down to retry.');
        setDbConnected(false);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setDbConnected(null);
    loadBooks(true);
  }, [loadBooks]);

  const categories = useMemo(() => {
    const set = new Set(books.map((b) => b.category).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [books]);

  const stats = useMemo(() => {
    const free = books.filter((b) => b.isFree).length;
    return { total: books.length, free, paid: books.length - free };
  }, [books]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return books.filter((b) => {
      if (filter === 'free' && !b.isFree) return false;
      if (filter === 'paid' && b.isFree) return false;
      if (category !== 'all' && b.category !== category) return false;
      if (q) {
        const hay = `${b.title} ${b.author} ${b.topics.join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [books, search, filter, category]);

  const openBook = (book) => {
    if (book.isFree && book.pdfUrl) {
      navigation.navigate('PDFViewer', { book });
    } else {
      navigation.navigate('BookDetails', { book });
    }
  };

  const quickAction = (book) => {
    if (book.isFree && book.pdfUrl) {
      navigation.navigate('PDFViewer', { book });
    } else if (book.amazonLink) {
      Linking.openURL(book.amazonLink).catch(() => {});
    } else {
      navigation.navigate('BookDetails', { book });
    }
  };

  const renderBook = ({ item: book }) => (
    <TouchableOpacity style={styles.card} activeOpacity={0.85} onPress={() => openBook(book)}>
      <View style={[styles.cover, { backgroundColor: book.coverImage ? '#eef2f7' : book.color }]}>
        {book.coverImage ? (
          <Image source={{ uri: book.coverImage }} style={styles.coverImg} resizeMode="cover" />
        ) : (
          <MaterialCommunityIcons name={book.icon || 'book'} size={40} color="#ffffff" />
        )}
        <View style={[styles.ribbon, { backgroundColor: book.isFree ? '#16A34A' : '#0e4e96' }]}>
          <Text style={styles.ribbonText}>{book.isFree ? 'FREE' : `£${book.price.toFixed(0)}`}</Text>
        </View>
      </View>

      <View style={styles.cardBody}>
        <Text style={styles.cardTitle} numberOfLines={2}>{book.title}</Text>
        <Text style={styles.cardAuthor} numberOfLines={1}>{book.author}</Text>

        <View style={styles.cardMetaRow}>
          <Ionicons name="star" size={12} color="#F59E0B" />
          <Text style={styles.cardMetaText}>{book.rating.toFixed(1)}</Text>
          <Text style={styles.cardMetaDot}>·</Text>
          <Text style={styles.cardMetaText} numberOfLines={1}>{book.level}</Text>
        </View>

        <TouchableOpacity
          style={[styles.cardBtn, { backgroundColor: book.isFree ? '#16A34A' : '#0e4e96' }]}
          onPress={() => quickAction(book)}
          activeOpacity={0.85}
        >
          <MaterialCommunityIcons
            name={book.isFree && book.pdfUrl ? 'book-open-variant' : 'cart-outline'}
            size={15}
            color="#ffffff"
          />
          <Text style={styles.cardBtnText}>{book.isFree && book.pdfUrl ? 'Read' : 'Buy'}</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderHeader = () => (
    <View>
      <View style={styles.statsRow}>
        <View style={styles.statBox}>
          <Text style={styles.statNum}>{stats.total}</Text>
          <Text style={styles.statLabel}>Books</Text>
        </View>
        <View style={[styles.statBox, styles.statBoxMid]}>
          <Text style={[styles.statNum, { color: '#16A34A' }]}>{stats.free}</Text>
          <Text style={styles.statLabel}>Free to Read</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={[styles.statNum, { color: '#0e4e96' }]}>{stats.paid}</Text>
          <Text style={styles.statLabel}>To Buy</Text>
        </View>
      </View>

      <View style={styles.searchBar}>
        <Ionicons name="search" size={18} color="#9CA3AF" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search title, author, topic…"
          placeholderTextColor="#9CA3AF"
          value={search}
          onChangeText={setSearch}
          returnKeyType="search"
        />
        {search ? (
          <TouchableOpacity onPress={() => setSearch('')}>
            <Ionicons name="close-circle" size={18} color="#9CA3AF" />
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={styles.filterRow}>
        {FILTERS.map((f) => (
          <TouchableOpacity
            key={f.key}
            style={[styles.filterPill, filter === f.key && styles.filterPillActive]}
            onPress={() => setFilter(f.key)}
            activeOpacity={0.8}
          >
            <Text style={[styles.filterText, filter === f.key && styles.filterTextActive]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {categories.length > 2 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.catScroll}>
          {categories.map((c) => (
            <TouchableOpacity
              key={c}
              style={[styles.catChip, category === c && styles.catChipActive]}
              onPress={() => setCategory(c)}
              activeOpacity={0.8}
            >
              <Text style={[styles.catChipText, category === c && styles.catChipTextActive]}>
                {c === 'all' ? 'All Categories' : c.charAt(0).toUpperCase() + c.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      <Text style={styles.resultCount}>
        {visible.length} {visible.length === 1 ? 'book' : 'books'}
        {filter === 'free' ? ' free to read' : filter === 'paid' ? ' to buy' : ''}
      </Text>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.empty}>
      <MaterialCommunityIcons name="book-search-outline" size={56} color="#D1D5DB" />
      <Text style={styles.emptyTitle}>No books found</Text>
      <Text style={styles.emptyText}>
        {search || filter !== 'all' || category !== 'all'
          ? 'Try clearing your search or filters.'
          : 'Pull down to refresh the library.'}
      </Text>
      {(search || filter !== 'all' || category !== 'all') && (
        <TouchableOpacity
          style={styles.clearBtn}
          onPress={() => { setSearch(''); setFilter('all'); setCategory('all'); }}
        >
          <Text style={styles.clearBtnText}>Clear filters</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" />

      <View style={styles.topBar}>
        <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#1f2937" />
        </TouchableOpacity>
        <Text style={styles.topTitle}>Engineering Library v99</Text>
        <View style={{ width: 24 }} />
      </View>

      {dbConnected !== null && (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={onRefresh}
          style={[styles.dbBadge, { backgroundColor: dbConnected ? '#E8F5E9' : '#FFF3E0' }]}
        >
          <View style={[styles.dbDot, { backgroundColor: dbConnected ? '#16A34A' : '#EF6C00' }]} />
          <Text style={[styles.dbBadgeText, { color: dbConnected ? '#16A34A' : '#EF6C00' }]}>
            {dbConnected ? 'Live · connected to JobEng' : 'Offline · showing saved list · tap to retry'}
          </Text>
        </TouchableOpacity>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#0e4e96" />
          <Text style={styles.loadingText}>Loading library…</Text>
        </View>
      ) : error ? (
        <View style={styles.center}>
          <MaterialCommunityIcons name="cloud-off-outline" size={56} color="#D1D5DB" />
          <Text style={styles.emptyTitle}>{error}</Text>
          <TouchableOpacity style={styles.clearBtn} onPress={() => loadBooks()}>
            <Text style={styles.clearBtnText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={visible}
          keyExtractor={(item, index) => String(item?.id ?? index)}
          renderItem={renderBook}
          numColumns={2}
          columnWrapperStyle={styles.columnWrap}
          ListHeaderComponent={renderHeader}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} tintColor="#0e4e96" />
          }
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  topBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#ffffff',
    borderBottomWidth: 1, borderBottomColor: '#EEF2F7',
  },
  topTitle: { fontSize: 18, fontWeight: '700', color: '#1f2937' },

  dbBadge: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'center',
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, marginTop: 10,
  },
  dbDot: { width: 8, height: 8, borderRadius: 4, marginRight: 7 },
  dbBadgeText: { fontSize: 12, fontWeight: '600' },

  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  loadingText: { marginTop: 12, color: '#6B7280', fontSize: 14 },

  listContent: { padding: 12, paddingBottom: 40 },
  columnWrap: { justifyContent: 'space-between' },

  statsRow: {
    flexDirection: 'row', backgroundColor: '#ffffff', borderRadius: 16,
    paddingVertical: 16, marginTop: 12, marginBottom: 14,
    shadowColor: '#0e4e96', shadowOpacity: 0.05, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  statBox: { flex: 1, alignItems: 'center' },
  statBoxMid: { borderLeftWidth: 1, borderRightWidth: 1, borderColor: '#F1F5F9' },
  statNum: { fontSize: 22, fontWeight: '800', color: '#1f2937' },
  statLabel: { fontSize: 11, color: '#6B7280', marginTop: 2 },

  searchBar: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#ffffff',
    borderRadius: 12, paddingHorizontal: 14, height: 46, marginBottom: 12,
    borderWidth: 1, borderColor: '#EEF2F7',
  },
  searchInput: { flex: 1, marginLeft: 8, fontSize: 15, color: '#1f2937' },

  filterRow: { flexDirection: 'row', marginBottom: 12 },
  filterPill: {
    paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20,
    backgroundColor: '#ffffff', marginRight: 8, borderWidth: 1, borderColor: '#E5E7EB',
  },
  filterPillActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  filterText: { fontSize: 13, fontWeight: '600', color: '#6B7280' },
  filterTextActive: { color: '#ffffff' },

  catScroll: { marginBottom: 12 },
  catChip: {
    paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16,
    backgroundColor: '#ffffff', marginRight: 8, borderWidth: 1, borderColor: '#E5E7EB',
  },
  catChipActive: { backgroundColor: '#EC4899', borderColor: '#EC4899' },
  catChipText: { fontSize: 12, fontWeight: '600', color: '#6B7280' },
  catChipTextActive: { color: '#ffffff' },

  resultCount: { fontSize: 13, color: '#9CA3AF', marginBottom: 8, marginLeft: 2 },

  card: {
    width: '48.5%', backgroundColor: '#ffffff', borderRadius: 16, marginBottom: 14,
    overflow: 'hidden', borderWidth: 1, borderColor: '#EEF2F7',
    shadowColor: '#0e4e96', shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 }, elevation: 1,
  },
  cover: { height: 130, alignItems: 'center', justifyContent: 'center', position: 'relative' },
  coverImg: { width: '100%', height: '100%' },
  ribbon: { position: 'absolute', top: 8, right: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  ribbonText: { color: '#ffffff', fontSize: 11, fontWeight: '800' },

  cardBody: { padding: 10 },
  cardTitle: { fontSize: 13.5, fontWeight: '700', color: '#1f2937', lineHeight: 18, minHeight: 36 },
  cardAuthor: { fontSize: 11.5, color: '#6B7280', marginTop: 3 },
  cardMetaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 6 },
  cardMetaText: { fontSize: 11, color: '#6B7280', marginLeft: 3 },
  cardMetaDot: { fontSize: 11, color: '#D1D5DB', marginHorizontal: 5 },

  cardBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderRadius: 10, paddingVertical: 8, marginTop: 10,
  },
  cardBtnText: { color: '#ffffff', fontSize: 13, fontWeight: '700', marginLeft: 6 },

  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60, paddingHorizontal: 30 },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: '#374151', marginTop: 14, textAlign: 'center' },
  emptyText: { fontSize: 14, color: '#9CA3AF', marginTop: 6, textAlign: 'center', lineHeight: 20 },
  clearBtn: { marginTop: 16, backgroundColor: '#0e4e96', paddingHorizontal: 22, paddingVertical: 10, borderRadius: 10 },
  clearBtnText: { color: '#ffffff', fontSize: 14, fontWeight: '700' },
});

export default FullTOC;
